import { useCallback, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import usePrevious from 'lib/src/hooks/usePrevious';
import useForm from '@components/form/hooks/useForm';

import { useHistory } from 'react-router-dom';
import {
    getChallengeGroupAdmins,
    getChallengeGroupsIsPosting,
    getChallengeGroupsPostError,
    getChallengeGroupsPostSuccess,
    getChallengeGroupViewers,
} from '@selectors/challengeGroups';
import { createChallengeGroup } from '@actions/challengeGroups/createChallengeGroup';
import useSite from 'lib/src/hooks/useSite';
import { site } from 'lib/src/shared/enums/site';
import {
    populateCreateChallengeGroupAdmins,
    populateCreateChallengeGroupViewers,
} from '@actions/challengeGroups';
import { ChallengeAdminOption } from 'src/types/shared/AdminUser';
import { ChallengeViewerOption } from 'src/types/shared/User';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';

export default function useCreateChallengeGroup() {
    const dispatch = useDispatch();
    const history = useHistory();
    const closeModal = useCallback(() => {
        history.push(`/challenge-groups`);
    }, [history]);
    const userSite = useSite();
    const [formState, handleChange] = useForm<FormState>({
        name: '',
        viewerIDs: [],
        adminIDs: [],
        site: userSite === site.ALL ? null : userSite,
    });

    const filterBySite = (user: ChallengeAdminOption | ChallengeViewerOption): boolean =>
        !!(user.site === 1000 || user.site === formState.site);

    const handleSubmit = useCallback(() => {
        const postBody = {
            ...formState,
            site: formState.site || site.IX,
        };
        dispatch(createChallengeGroup(postBody));
    }, [dispatch, formState]);

    const isPosting = useSelector(getChallengeGroupsIsPosting);
    const error = useSelector(getChallengeGroupsPostError);
    const postSuccess = useSelector(getChallengeGroupsPostSuccess);
    const prevPostSuccess = usePrevious(postSuccess);
    const admins = useSelector(getChallengeGroupAdmins) || [];
    const viewers = useSelector(getChallengeGroupViewers) || [];

    const filteredAdmins = admins.filter(filterBySite);
    const filteredViewers = viewers.filter(filterBySite);

    useEffect(() => {
        batch(() => {
            dispatch(populateCreateChallengeGroupAdmins());
            dispatch(populateCreateChallengeGroupViewers());
        });
    }, [dispatch]);

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            // todo redirect to newly created?
            closeModal();
        }
    }, [postSuccess, prevPostSuccess, closeModal]);

    return {
        handleSubmit,
        formState,
        handleChange,
        closeModal,
        isPosting,
        error,
        adminOptions: filteredAdmins
            .sort((a, b) => a.lastName.localeCompare(b.lastName))
            .map(getUserOption),
        viewerOptions: filteredViewers
            .sort((a, b) => a.lastName.localeCompare(b.lastName))
            .map(getUserOption),
    };
}

const getUserOption = (
    user: ChallengeAdminOption | ChallengeViewerOption,
): DropdownOption<number> => ({
    value: user.id,
    label: user.firstName + ' ' + user.lastName,
});

interface FormState {
    name: string;
    viewerIDs: number[];
    adminIDs: number[];
    site: site | null;
}
